import { UserEventContext } from "api/userEvents";

import { IScreenshotSettings } from "models/screenshot";

/**
 * Interface for widget-specific context extractors
 * Each widget type can implement this to extract relevant context for screenshot events
 */
export interface WidgetContextExtractor {
  /**
   * The widget component type this extractor handles
   */
  widgetType: string;

  /**
   * Function to extract widget-specific context for screenshot events
   * @param widgetState The current state of the widget
   * @param settings The screenshot settings being used
   * @returns Additional context specific to this widget type
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extractContext(widgetState: any, settings: IScreenshotSettings): UserEventContext;
}

/**
 * Registry for widget context extractors
 */
class WidgetContextExtractorRegistry {
  private extractors = new Map<string, WidgetContextExtractor>();

  /**
   * Register a context extractor for a specific widget type
   */
  register(extractor: WidgetContextExtractor) {
    this.extractors.set(extractor.widgetType, extractor);
  }

  /**
   * Extract context for a specific widget type
   * @param widgetType The type of widget
   * @param widgetState The current state of the widget
   * @param settings The screenshot settings being used
   * @returns Widget-specific context or empty object if no extractor registered
   */
  extractContext(
    widgetType: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    widgetState: any,
    settings: IScreenshotSettings
  ): UserEventContext {
    const extractor = this.extractors.get(widgetType);
    if (!extractor) {
      return {};
    }

    try {
      return extractor.extractContext(widgetState, settings);
    } catch (error) {
      // Silently handle errors to prevent screenshot functionality from breaking
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.error(`Failed to extract widget context for ${widgetType}:`, error);
      }
      return {};
    }
  }

  /**
   * Check if an extractor is registered for a widget type
   */
  hasExtractor(widgetType: string): boolean {
    return this.extractors.has(widgetType);
  }

  /**
   * Get all registered widget types
   */
  getRegisteredTypes(): string[] {
    return Array.from(this.extractors.keys());
  }
}

// Singleton instance
export const widgetContextExtractorRegistry = new WidgetContextExtractorRegistry();

/**
 * Convenience function to extract widget-specific context for screenshots
 * @param widgetType The type of widget (e.g., "chart", "map", "xdaViewer")
 * @param widgetState The current state of the widget
 * @param settings The screenshot settings being used
 * @returns Widget-specific context for the screenshot event
 */
export function extractWidgetScreenshotContext(
  widgetType: string | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  widgetState: any,
  settings: IScreenshotSettings
): UserEventContext {
  if (!widgetType) {
    return {};
  }

  return widgetContextExtractorRegistry.extractContext(widgetType, widgetState, settings);
}
